import clsx from 'clsx';
import { supportArticles } from 'lightning-common/lib/configuration';
import { getUrl, Routes } from 'lightning-common/lib/routing';
import { getSupportUrl } from 'lightning-common/lib/support-url';
import { useState } from 'react';

import { useUserInfo } from '../../hooks/use-user-info';
import { getHtmlCopy } from '../../i18n';
import { forwardUrl } from '../../utilities/forward-url';
import { LogoOverDriveWhite } from '../logos';
import {
    SocialBadgeFacebook,
    SocialBadgePinterest,
    SocialBadgeTwitter,
    SocialBadgeYouTube,
} from '../social';
import { SupportChoiceModal } from '../support-choice/SupportChoiceModal';
import { ReactComponent as BCorpWhite } from './b-corp-white.svg';
import { FooterLinksColumn, FooterColumn } from './FooterColumn';
import { FooterLink } from './FooterLink';
import { FooterLinkButton } from './FooterLinkButton';
import { FooterLibbyPromoColumn } from './FooterLibbyPromoColumn';
import { FooterSoraPromoColumn } from './FooterSoraPromoColumn';
import { FooterShareLinks, FooterShareLink } from './FooterShareLink';

import './FooterRowWebsiteLinks.scss';

export interface FooterRowWebsiteLinksProps {
    disableWishlist?: boolean;
    gacEnabled?: boolean;
    hasLinkedCards?: boolean;
    isAuthenticated?: boolean;
    isConsortium?: boolean;
    isDlr?: boolean;
    libraryHomeUrl?: string;
    libraryKey: string;
    libraryName: string;
    librarySupportEmail?: string;
    librarySupportUrl?: string;
    ntcMode?: boolean;
    openLinksInNewWindow?: boolean;
    optimizeForChina?: boolean;
    sampleOnlyMode?: boolean;
    showHolds?: boolean;
    showKindlePromo?: boolean;
    showLibbyPromo?: boolean;
    showOverDriveWindowsLink?: boolean;
    showSoraPromo?: boolean;
    showReadingHistory?: boolean;
    simplifiedNavigationBar?: boolean;
    socialFacebookUrl?: string;
    socialPinterestUrl?: string;
    socialTwitterUrl?: string;
    socialYouTubeUrl?: string;
}

export const FooterRowWebsiteLinks = ({
    disableWishlist,
    gacEnabled,
    hasLinkedCards,
    isAuthenticated,
    isConsortium,
    isDlr,
    libraryHomeUrl,
    libraryKey,
    libraryName,
    librarySupportEmail,
    librarySupportUrl,
    ntcMode,
    openLinksInNewWindow,
    sampleOnlyMode,
    showHolds,
    showKindlePromo,
    showLibbyPromo,
    showOverDriveWindowsLink,
    showSoraPromo,
    showReadingHistory,
    simplifiedNavigationBar,
    socialFacebookUrl,
    socialPinterestUrl,
    socialTwitterUrl,
    socialYouTubeUrl,
}: FooterRowWebsiteLinksProps) => {
    const { locale } = useUserInfo();

    const [supportChoiceModalOpen, setSupportChoiceModalOpen] = useState(false);
    const closeSupportChoiceModal = () => setSupportChoiceModalOpen(false);
    const openSupportChoiceModal = () => setSupportChoiceModalOpen(true);

    const showStayConnectedColumn = !!(
        isConsortium ||
        (!isConsortium && libraryHomeUrl) ||
        socialFacebookUrl ||
        socialPinterestUrl ||
        socialTwitterUrl ||
        socialYouTubeUrl
    );

    let rowTopColumns = sampleOnlyMode ? 0 : ntcMode ? 1 : 3;
    !sampleOnlyMode && !ntcMode && showStayConnectedColumn && rowTopColumns++;
    showLibbyPromo && rowTopColumns++;
    showSoraPromo && rowTopColumns++;

    const kindleVisible = showKindlePromo && !sampleOnlyMode && !ntcMode;

    return (
        <>
            <div
                className={clsx(
                    'footer__row--top',
                    `footer__row--top-cols-${rowTopColumns}`,
                    sampleOnlyMode && 'footer__row--top-sample-only',
                    sampleOnlyMode &&
                        !kindleVisible &&
                        'footer__row--top-sample-only-no-kindle'
                )}
            >
                {!sampleOnlyMode && (
                    <FooterColumn classes="footer__column-corporate">
                        <a
                            className="footer__column-corporate-logo-link"
                            href="https://www.overdrive.com"
                            rel={
                                openLinksInNewWindow ? 'noreferrer' : undefined
                            }
                            target={openLinksInNewWindow ? '_blank' : undefined}
                        >
                            <LogoOverDriveWhite
                                aria-label={getHtmlCopy(
                                    'footer.overdriveLinkAlt'
                                )}
                            />
                        </a>

                        <a
                            href="https://company.overdrive.com/company-profile/overdrive-b-corporation"
                            rel={
                                openLinksInNewWindow ? 'noreferrer' : undefined
                            }
                            target={openLinksInNewWindow ? '_blank' : undefined}
                        >
                            <BCorpWhite
                                aria-label={getHtmlCopy('footer.bcorpLinkAlt')}
                            />
                        </a>
                    </FooterColumn>
                )}

                {!sampleOnlyMode && !ntcMode && (
                    <FooterLinksColumn
                        classes="footer__column-my-account"
                        heading={getHtmlCopy('myAccount')}
                    >
                        {isAuthenticated && (
                            <>
                                <FooterLink
                                    href={getUrl(Routes.accountLoans)}
                                    text={getHtmlCopy('loans')}
                                />

                                {showHolds && (
                                    <FooterLink
                                        href={getUrl(Routes.accountHolds)}
                                        text={getHtmlCopy('holds')}
                                    />
                                )}

                                {!disableWishlist && (
                                    <FooterLink
                                        href={getUrl(Routes.accountWishlist)}
                                        text={getHtmlCopy('wishlist')}
                                    />
                                )}

                                {showReadingHistory && (
                                    <FooterLink
                                        href={getUrl(Routes.accountHistory)}
                                        text={getHtmlCopy(
                                            'readingHistory.history'
                                        )}
                                    />
                                )}
                                {!simplifiedNavigationBar && (
                                    <FooterLink
                                        href={getUrl(Routes.accountSettings)}
                                        text={getHtmlCopy('settings')}
                                    />
                                )}
                                <FooterLink
                                    href={getUrl(Routes.accountSignout)}
                                    text={getHtmlCopy('signOut')}
                                />
                            </>
                        )}

                        {!isAuthenticated && (
                            <FooterLink
                                href={forwardUrl(getUrl(Routes.accountSignIn))}
                                text={getHtmlCopy('signIn')}
                            />
                        )}

                        {isDlr &&
                            !simplifiedNavigationBar &&
                            (!isAuthenticated || !hasLinkedCards) && (
                                <>
                                    {gacEnabled && (
                                        <FooterLink
                                            href={getUrl(
                                                Routes.accountStartCardLink,
                                                { intent: 'gac' }
                                            )}
                                            text={getHtmlCopy(
                                                'needALibraryCard'
                                            )}
                                        />
                                    )}

                                    {!gacEnabled && (
                                        <FooterLink
                                            href={getUrl(Routes.supportCard)}
                                            text={getHtmlCopy(
                                                'needALibraryCard'
                                            )}
                                        />
                                    )}
                                </>
                            )}
                    </FooterLinksColumn>
                )}

                {!sampleOnlyMode && !ntcMode && (
                    <FooterLinksColumn
                        classes="footer__column-support"
                        heading={getHtmlCopy('support')}
                    >
                        <FooterLink
                            href={getSupportUrl(
                                locale,
                                supportArticles.havingTrouble,
                                { libraryKey, librarySupportUrl, isEdge: true }
                            )}
                            openInNewWindow={openLinksInNewWindow}
                            text={getHtmlCopy('help')}
                        />

                        <FooterLinkButton
                            label={getHtmlCopy('getSupport')}
                            onClick={openSupportChoiceModal}
                        />
                    </FooterLinksColumn>
                )}

                {!sampleOnlyMode && !ntcMode && showStayConnectedColumn && (
                    <FooterLinksColumn
                        classes="footer__column-stay-connected"
                        heading={getHtmlCopy('stayConnected')}
                    >
                        {isConsortium && (
                            <FooterLink
                                href={getUrl(Routes.supportMembers)}
                                text={getHtmlCopy('libraryLinks')}
                            />
                        )}

                        {!isConsortium && libraryHomeUrl && (
                            <FooterLink
                                href={libraryHomeUrl}
                                openInNewWindow
                                text={`${libraryName} ${getHtmlCopy(
                                    'libraryHome'
                                )}`}
                            />
                        )}

                        <FooterShareLinks>
                            {socialFacebookUrl && (
                                <FooterShareLink
                                    badge={<SocialBadgeFacebook />}
                                    href={socialFacebookUrl}
                                    openInNewWindow={openLinksInNewWindow}
                                    title={getHtmlCopy('footer.visitFacebook')}
                                />
                            )}

                            {socialTwitterUrl && (
                                <FooterShareLink
                                    badge={<SocialBadgeTwitter />}
                                    href={socialTwitterUrl}
                                    openInNewWindow={openLinksInNewWindow}
                                    title={getHtmlCopy('footer.visitTwitter')}
                                />
                            )}

                            {socialYouTubeUrl && (
                                <FooterShareLink
                                    badge={<SocialBadgeYouTube />}
                                    href={socialYouTubeUrl}
                                    openInNewWindow={openLinksInNewWindow}
                                    title={getHtmlCopy('footer.visitYoutube')}
                                />
                            )}

                            {socialPinterestUrl && (
                                <FooterShareLink
                                    badge={<SocialBadgePinterest />}
                                    href={socialPinterestUrl}
                                    openInNewWindow={openLinksInNewWindow}
                                    title={getHtmlCopy('footer.visitPinterest')}
                                />
                            )}
                        </FooterShareLinks>
                    </FooterLinksColumn>
                )}

                {showLibbyPromo && (
                    <FooterLibbyPromoColumn
                        showKindlePromo={kindleVisible}
                        ntcMode={ntcMode}
                    />
                )}

                {showSoraPromo && (
                    <FooterSoraPromoColumn showKindlePromo={kindleVisible} />
                )}
            </div>

            <SupportChoiceModal
                isConsortium={isConsortium}
                libraryKey={libraryKey}
                librarySupportEmail={librarySupportEmail}
                librarySupportUrl={librarySupportUrl}
                onClose={closeSupportChoiceModal}
                open={supportChoiceModalOpen}
            />
        </>
    );
};
